import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { FiUsers } from "react-icons/fi";
import { getReferral } from "./MyReferral.api";
import Pagination from "components/Pagination/Pagination";
import { Referral } from "./MyReferral.interface";
import { useAuth } from "context/auth-context";
import { dateFormatHandler, YYYY_MM_DD } from "./../../lib/date-formatter-moment";
import { Badge, EmptyState, SkeletonRows } from "components/ui";

function getStatusReferral(status: any) {
  let btn = status;
  switch (status) {
    case 1:
      btn = (
        <Badge tone="success" dot>
          Credited
        </Badge>
      );
      break;
    case 0:
    default:
      btn = (
        <Badge tone="warning" dot>
          In progress
        </Badge>
      );
      break;
  }
  return btn;
}

type Props = {
  setReferralCount: (value: number) => void;
};

const CardHeader = ({ title }: { title: React.ReactNode }) => (
  <div className="zs-card-header">
    <div className="min-w-0">
      <h2 className="zs-card-title">{title}</h2>
      <p className="mt-0.5 zs-card-subtitle">Friends who joined with your link and their reward status.</p>
    </div>
  </div>
);

export const ReferralUserTable = ({ setReferralCount }: Props) => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const [currentReferralPage, setCurrentReferralPage] = useState<number>(1);

  const getReferralData = useQuery(
    ["referral", currentReferralPage],
    () => getReferral(auth.panelistId, currentReferralPage),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    getReferralData.refetch();
  }, [currentReferralPage]);

  if (getReferralData.isLoading) {
    return (
      <section className="zs-card">
        <CardHeader title={t("referFriend.My Referral Users")} />
        <span className="sr-only">{t("page.referral.Loading")}</span>
        <SkeletonRows rows={5} />
      </section>
    );
  }

  const page = 5;
  const startIndex = (currentReferralPage - 1) * page;
  setReferralCount(getReferralData.data?.result?.totalUsers || 0);

  return (
    <section className="zs-card">
      <CardHeader title={t("referFriend.My Referral Users")} />
      <div data-testid="referral" className="zs-table-wrap is-flush">
        <table className="zs-table">
          <thead>
            <tr>
              <th scope="col" className="w-16">
                {t("pages.referral.SrNo")}
              </th>
              <th scope="col">{t("page.referral.Email")}</th>
              <th scope="col" className="is-num">
                {t("page.referral.Points")}
              </th>
              <th scope="col">{t("page.referral.Status")}</th>
              <th scope="col">{t("page.referral.Date")}</th>
            </tr>
          </thead>
          <tbody>
            {getReferralData.data?.result?.data?.length > 0 ? (
              getReferralData.data?.result?.data.map((survey: Referral, i: number) => {
                return (
                  <tr key={startIndex + i + 1}>
                    <td className="tabular-nums text-ink-3">{startIndex + i + 1}</td>
                    <td className="is-strong whitespace-nowrap" title={survey.user_email}>
                      {survey?.user_email?.substring(0, 12) + "..."}
                    </td>
                    <td className="is-num is-strong">{Math.floor(+survey.points)}</td>
                    <td>{getStatusReferral(survey.isRewardAdded)}</td>
                    <td className="whitespace-nowrap text-ink-3">{dateFormatHandler(survey.createdAt, YYYY_MM_DD)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className="!p-0">
                  <EmptyState
                    icon={<FiUsers />}
                    title={t("page.mysurvey.nodata")}
                    description="Share your referral link. Friends who sign up will appear here."
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {getReferralData.data?.result && getReferralData.data?.result?.data?.length > 0 ? (
        <div className="zs-table-footer">
          <span className="tabular-nums">
            Page {currentReferralPage} of {getReferralData?.data?.result?.totalPages}
          </span>
          <Pagination
            setCurrentPage={setCurrentReferralPage}
            totalPages={getReferralData?.data?.result?.totalPages}
            currentPage={currentReferralPage}
          />
        </div>
      ) : null}
    </section>
  );
};
